import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form, Modal, Alert, Badge, Spinner } from 'react-bootstrap';
// Auth context is automatically used by the api utility
import api from '../../services/api';

/**
 * CustomDomains component for managing custom domains
 * Allows users to add, verify and delete custom domains
 */
const CustomDomains = () => {
  const [domains, setDomains] = useState([]);
  const [maxDomains, setMaxDomains] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load domains on component mount
  useEffect(() => {
    fetchDomains();
  }, []);

  // Fetch domains from API
  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pro-features/custom-domains');
      setDomains(response.data.data.domains || []);
      setMaxDomains(response.data.data.maxDomains || 5);
      setError(null);
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Failed to load your custom domains. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Add a new domain
  const handleAddDomain = async (e) => {
    e.preventDefault();
    setAddingDomain(true);
    
    try {
      const response = await api.post('/pro-features/custom-domains', { 
        domainName: newDomain 
      });
      
      // Add the new domain to the list
      setDomains([...domains, response.data.data.domain]);
      setNewDomain('');
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error adding domain:', err);
      setError(err.response?.data?.message || 'Failed to add domain. Please try again.');
    } finally {
      setAddingDomain(false);
    }
  };

  // Verify a domain
  const handleVerifyDomain = async () => {
    if (!currentDomain) return;
    
    setVerifying(true);
    setVerifyError(null);
    
    try {
      const response = await api.post(`/pro-features/custom-domains/${currentDomain.id}/verify`);
      
      // Update the domain in the list
      const updatedDomains = domains.map(domain => 
        domain.id === currentDomain.id ? response.data.data.domain : domain
      );
      
      setDomains(updatedDomains);
      setShowVerifyModal(false);
    } catch (err) {
      console.error('Error verifying domain:', err);
      setVerifyError(err.response?.data?.message || 'Verification failed. Please check your DNS settings and try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Delete a domain
  const handleDeleteDomain = async () => {
    if (!currentDomain) return;
    
    setDeleting(true);
    
    try {
      await api.delete(`/pro-features/custom-domains/${currentDomain.id}`);
      
      // Remove the domain from the list
      const filteredDomains = domains.filter(domain => domain.id !== currentDomain.id);
      setDomains(filteredDomains);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting domain:', err);
      setError('Failed to delete domain. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Get status badge for a domain
  const getStatusBadge = (status) => {
    switch(status) {
      case 'verified':
        return <Badge bg="success">Verified</Badge>;
      case 'dns_verified':
        return <Badge bg="warning">DNS Verified (CNAME Needed)</Badge>;
      case 'pending_verification':
        return <Badge bg="warning">Pending Verification</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Custom Domains</h5>
        <Button 
          variant="primary" 
          size="sm"
          onClick={() => setShowAddModal(true)}
          disabled={domains.length >= maxDomains}
        >
          Add Domain
        </Button>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : domains.length === 0 ? (
          <Alert variant="info">
            You haven't added any custom domains yet. Add your first domain to create branded links.
          </Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Domain Name</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map(domain => (
                <tr key={domain.id}>
                  <td>{domain.domainName}</td>
                  <td>{getStatusBadge(domain.status)}</td>
                  <td>{new Date(domain.createdAt).toLocaleDateString()}</td>
                  <td>
                    {domain.status !== 'verified' && (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setCurrentDomain(domain);
                          setShowVerifyModal(true);
                        }}
                      >
                        Verify
                      </Button>
                    )}
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        setCurrentDomain(domain);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="mt-3">
          <h6>Using Custom Domains</h6>
          <p>
            When you verify a custom domain, you can use it to create branded affiliate links.
            Your visitors will see your domain instead of the default tracking domain.
          </p>
          
          <h6>Verification Instructions:</h6>
          <ol>
            <li>Add a domain in your DNS provider's dashboard</li>
            <li>
              Create a TXT record for verification:
              <ul>
                <li><strong>Type:</strong> TXT</li>
                <li><strong>Name:</strong> _affiliate-verify</li>
                <li><strong>Value:</strong> The verification code we provide</li>
              </ul>
            </li>
            <li>
              Create a CNAME record to point your domain to our servers:
              <ul>
                <li><strong>Type:</strong> CNAME</li>
                <li><strong>Name:</strong> @ or subdomain</li>
                <li><strong>Value:</strong> affiliate-tracking-saas-api.onrender.com</li>
              </ul>
            </li>
            <li>Click "Verify" when the DNS changes have propagated (this can take up to 48 hours)</li>
          </ol>
        </div>
      </Card.Body>

      {/* Add Domain Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Custom Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddDomain}>
            <Form.Group className="mb-3">
              <Form.Label>Domain Name</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="example.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Enter your domain without http:// or https://.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={addingDomain}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddDomain}
            disabled={addingDomain || !newDomain}
          >
            {addingDomain ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Adding...</span>
              </>
            ) : 'Add Domain'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Verify Domain Modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Verify Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {verifyError && <Alert variant="danger">{verifyError}</Alert>}
          
          <p>To verify your domain <strong>{currentDomain?.domainName}</strong>, follow these steps:</p>
          
          {currentDomain && (
            <>
              <ol>
                <li>
                  <p>Add the following TXT record to your DNS configuration:</p>
                  <div className="bg-light p-2 rounded">
                    <p className="mb-1"><strong>Type:</strong> TXT</p>
                    <p className="mb-1"><strong>Host/Name:</strong> {currentDomain.dnsRecord?.name.replace(currentDomain.domainName, '@')}</p>
                    <p className="mb-0"><strong>Value/Content:</strong> {currentDomain.dnsRecord?.value}</p>
                  </div>
                </li>
                <li>
                  <p>Add the following CNAME record to your DNS configuration:</p>
                  <div className="bg-light p-2 rounded">
                    <p className="mb-1"><strong>Type:</strong> CNAME</p>
                    <p className="mb-1"><strong>Host/Name:</strong> @</p>
                    <p className="mb-0"><strong>Value/Target:</strong> affiliate-tracking-saas-api.onrender.com</p>
                  </div>
                </li>
                <li>
                  <p>Click the "Verify" button below after your DNS changes have propagated.</p>
                  <p className="text-muted">DNS changes may take up to 48 hours to fully propagate across the internet.</p>
                </li>
              </ol>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)} disabled={verifying}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleVerifyDomain}
            disabled={verifying}
          >
            {verifying ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Verifying...</span>
              </>
            ) : 'Verify Domain'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Domain Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the domain <strong>{currentDomain?.domainName}</strong>?</p>
          <Alert variant="warning">
            This will remove the domain from your account and all links using this domain will revert to the default tracking domain.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteDomain}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Deleting...</span>
              </>
            ) : 'Delete Domain'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default CustomDomains;
